import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { Text } from "@sitecore-jss/sitecore-jss-react";
import ModalPanel from "@clarion/foundation/dist/Helpers/containers/ModalPanel";
import EmailInput from "@clarion/foundation/dist/Helpers/components/Forms/EmailInput";
import PasswordInput from "@clarion/foundation/dist/Helpers/components/Forms/PasswordInput";
import GlobalErrorComponent from "@clarion/foundation/dist/Helpers/components/Forms/GlobalErrorComponent";
import ModalLoader from "@clarion/foundation/dist/Helpers/components/ModalLoader";
import ModalImagery from "@clarion/foundation/dist/Helpers/components/ModalImagery";
import { userDataApiCall } from "@clarion/foundation/dist/Utils/apiUtils";
import { toggleLoginModal, toggleForgottenPasswordModal, toggleRegisterAccountModal, toggleUnlockAccountModal } from "@clarion/foundation/dist/Settings/actions/globalActions";
import { PERSONALISATIONPOSTCODE_COOKIE } from "@clarion/foundation/dist/Settings/constants/cookies";
import { cookies } from "@clarion/foundation/dist/Utils/cookieUtils";
import { invalidFieldError } from "@clarion/foundation/dist/Settings/constants/forms";
import { openChatbot } from "@clarion/foundation/dist/Utils/genericUtils";
import "@clarion/foundation/dist/Theming/styles/forms.css";
import "@clarion/foundation/dist/Theming/styles/account.css";
const LoginModal = ({
  setOpenMigrationModal,
  fields,
  rendering,
  t,
  sitecoreContext
}) => {
  const global = useSelector(state => state.global);
  const modal = useSelector(state => state.modal);
  const cookie = useSelector(state => state.cookie);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    errors
  } = useForm();
  const [showLoader, setShowLoader] = useState(false);
  const [showGlobalError, setShowGlobalError] = useState(false);
  const [showExceededError, setShowExceededError] = useState(false);
  const [showSystemError, setShowSystemError] = useState(false);
  const [email, setEmail] = useState("");
  const emailRegex = new RegExp(fields.RegexSettings.fields.Email);
  const unlockAccountFeatureToggle = sitecoreContext.unlockAccountSettings?.unlockAccountEnabled;
  const requiredLabel = t("FieldRequired");
  const showPasswordLabel = t("ShowPassword");
  const hidePasswordLabel = t("HidePassword");
  const formFields = {
    email: "Email",
    password: "Password"
  };
  const clearRepairsJourneySession = () => {
    window.sessionStorage.removeItem("Repairs");
  };
  const serviceCall = data => {
    var apiPath = "/api/account/login-user";
    const federatedLink = window.sessionStorage.getItem("federatedLink");
    if (federatedLink) {
      window.sessionStorage.removeItem("federatedLink");
      apiPath = "/api/account/login-user-federated-link";
    }
    userDataApiCall(apiPath, rendering, data).then(response => {
      clearRepairsJourneySession();

      // Set cookie to enable location personalisation
      if (response.UserData && response.UserData.Postcode && cookie.isPreferencesEnabled) {
        cookies.set({
          name: PERSONALISATIONPOSTCODE_COOKIE,
          value: response.UserData.Postcode
        });
      }
      if (response.LoginStatusCode === "Success") {
        const loginRedirect = new URLSearchParams(window.location.search).get("redirectUrl") || window.sessionStorage.getItem("loginRedirect");
        if (loginRedirect) {
          window.sessionStorage.removeItem("loginRedirect");
          window.location.href = loginRedirect;
        } else {
          window.location.href = response.RedirectUrl || "/";
        }
        return false;
      }
      if (response.LoginStatusCode === "ChangePassword") {
        userDataApiCall("/api/account/reset-password-send-email", rendering, data).then(() => {
          dispatch(toggleLoginModal(false));
          setOpenMigrationModal(true);
        }).catch(() => {
          setShowGlobalError(true);
        }).finally(() => {
          setShowLoader(false);
        });
        return false;
      }
      setShowLoader(false);
      setShowGlobalError(true);
    }).catch(statusCode => {
      switch (statusCode) {
        case 403:
          {
            // Exceeded number of login attempts
            if (unlockAccountFeatureToggle) {
              window.sessionStorage.setItem("loginEmail", email);
              setEmail("");
              dispatch(toggleLoginModal(false));
              dispatch(toggleUnlockAccountModal(true));
              break;
            }
            openChatbot();
            setShowExceededError(true);
            break;
          }
        case 503:
          {
            // System availability issue
            openChatbot();
            setShowSystemError(true);
            break;
          }
        default:
          {
            setShowGlobalError(true);
          }
      }
      setShowLoader(false);
    });
  };
  const onSubmit = data => {
    if (!unlockAccountFeatureToggle) {
      setShowExceededError(false);
    }
    setShowGlobalError(false);
    setShowSystemError(false);
    setShowLoader(true);

    // Delay service call to ensure loader always displays
    setTimeout(() => serviceCall(data), 500);
  };
  const closeModal = () => {
    setShowLoader(false);
    dispatch(toggleLoginModal(false));
    window.sessionStorage.removeItem("loginEmail");
  };
  const handleForgotten = () => {
    window.sessionStorage.setItem("loginEmail", email);
    dispatch(toggleLoginModal(false));
    dispatch(toggleForgottenPasswordModal(true));
    setEmail("");
  };
  const openRegisterModal = () => {
    dispatch(toggleRegisterAccountModal(true));
    dispatch(toggleLoginModal(false));
  };
  const afterClose = () => {
    if (!global.isTablet && window.sessionStorage.getItem("refocusNav") === "true") {
      window.document.getElementsByClassName("navigation__trigger")[0].focus();
      window.sessionStorage.removeItem("refocusNav");
    }
  };
  return /*#__PURE__*/React.createElement(ModalPanel, {
    isOpen: modal.isLoginOpen,
    onClose: closeModal,
    contentLabel: fields.Heading.value,
    contentClassName: "modal__content--account",
    closeClassName: "qa-login__close account__close",
    afterClose: afterClose
  }, /*#__PURE__*/React.createElement("div", {
    className: "qa-login account"
  }, /*#__PURE__*/React.createElement("div", {
    className: "account__primary-content"
  }, /*#__PURE__*/React.createElement(ModalImagery, null), showLoader && /*#__PURE__*/React.createElement(ModalLoader, {
    label: fields.LoadingLabel.value
  }), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    tag: "h2",
    className: `qa-login__header account__header ${showLoader ? "account--hide" : ""}`,
    field: fields.Heading
  }), showGlobalError && /*#__PURE__*/React.createElement(GlobalErrorComponent, {
    message: fields.LoginErrorTryAgain.value,
    title: fields.LoginErrorHeading.value
  }), !unlockAccountFeatureToggle && showExceededError && /*#__PURE__*/React.createElement(GlobalErrorComponent, {
    message: fields.LoginErrorAttemptLimit.value
  }), showSystemError && /*#__PURE__*/React.createElement(GlobalErrorComponent, {
    message: fields.LoginErrorSystemIssue.value
  }), /*#__PURE__*/React.createElement("form", {
    className: `${showLoader ? "account--hide" : ""}`,
    onSubmit: handleSubmit(onSubmit)
  }, /*#__PURE__*/React.createElement(EmailInput, {
    ref: register({
      required: requiredLabel,
      pattern: {
        value: emailRegex,
        message: fields.RegexSettings.fields.EmailError.value || invalidFieldError
      }
    }),
    autoFocus: !global.isTablet,
    isGeneric: false,
    isRequired: true,
    fieldName: formFields.email,
    setEmail: setEmail,
    className: "qa-login__email account__form-email",
    labelClassName: "qa-login__email-label",
    labelName: fields.EmailAddress.value,
    placeholder: fields.EmailAddress.value,
    id: "login-email-field",
    errors: errors[formFields.email],
    describedBy: "login-email-field-error"
  }), /*#__PURE__*/React.createElement(PasswordInput, {
    ref: register({
      required: requiredLabel
    }),
    isGeneric: false,
    isRequired: true,
    fieldName: formFields.password,
    id: "login-password",
    className: "qa-login__password account__form-password",
    labelClassName: "qa-login__password-label",
    labelName: fields.Password.value,
    showLabel: showPasswordLabel,
    hideLabel: hidePasswordLabel,
    placeholder: fields.Password.value,
    errors: errors[formFields.password]
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    tag: "p",
    className: "account__lock-warning copy-small",
    field: fields.LockedAccountWarning
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "account__forgotten-password general-link general-link--small",
    onClick: handleForgotten
  }, fields.ForgottenPasswordCTA.value)), /*#__PURE__*/React.createElement("button", {
    className: "account__btn btn btn--small",
    type: "submit"
  }, fields.LoginCTA.value)))), /*#__PURE__*/React.createElement("div", {
    className: `account__register ${showLoader ? "account--hide" : ""}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "account__register-content"
  }, /*#__PURE__*/React.createElement(Text, {
    tag: "h5",
    className: "account__register-title",
    field: fields.RegisterHeading
  }), /*#__PURE__*/React.createElement(Text, {
    tag: "p",
    className: "account__register-text copy-small",
    field: fields.RegisterSubHeading
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("button", {
    onClick: openRegisterModal,
    type: "button",
    className: "account__register-btn btn btn--register btn--secondary btn--small"
  }, fields.RegisterCTA.value)))));
};
export default LoginModal;